import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import SwipeLoop from "@/components/landing/SwipeLoop";

const SHOTS = [
  { src: ASSETS.screenshots.onboarding, label: "Bienvenue" },
  { src: ASSETS.screenshots.swipe, label: "Découverte" },
  { src: ASSETS.screenshots.chat, label: "Chat privé" },
  { src: ASSETS.screenshots.events, label: "Événements" },
];

const EASE = [0.2, 0.7, 0.2, 1];

export default function Screenshots() {
  const [index, setIndex] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => {
      const items = Array.from(el.children);
      const center = el.scrollLeft + el.clientWidth / 2;
      let closest = 0;
      let dist = Infinity;
      items.forEach((it, k) => {
        const c = it.offsetLeft + it.clientWidth / 2;
        const d = Math.abs(c - center);
        if (d < dist) {
          dist = d;
          closest = k;
        }
      });
      setIndex(closest);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (i) => {
    const clamped = Math.max(0, Math.min(SHOTS.length - 1, i));
    const el = trackRef.current?.children[clamped];
    if (el)
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
  };

  return (
    <section
      id="apercu"
      data-testid="screenshots-section"
      className="relative py-20 md:py-32 bg-white overflow-hidden"
    >
      <div className="mx-auto max-w-[1280px] px-5 md:px-8">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-center mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-7 order-2 md:order-1">
            <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#D97757]">
              Aperçu de l'app
            </p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: EASE }}
              className="mt-4 font-display text-[32px] sm:text-[42px] md:text-[52px] text-[#0A0A0A]"
            >
              Un swipe. Une maman. Une vraie rencontre.
            </motion.h2>
            <p className="mt-5 max-w-[500px] text-[15px] md:text-[17px] leading-[1.6] text-[#6B6660]">
              Découvre des mamans près de chez toi, filtrées selon ta ville,
              ton étape et l'âge de ton enfant. Un geste — et la conversation
              commence.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 md:gap-6 max-w-[440px]">
              {[
                { k: "Local", v: "Rue, quartier, ville" },
                { k: "Filtré", v: "Étape, âge, situation" },
                { k: "Simple", v: "En 2 minutes" },
              ].map((s) => (
                <div key={s.k} className="border-t border-[rgba(10,10,10,0.1)] pt-3">
                  <div className="text-[13px] font-semibold text-[#0A0A0A]">
                    {s.k}
                  </div>
                  <div className="text-[12.5px] text-[#6B6660] mt-1 leading-snug">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 order-1 md:order-2 flex justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.9, ease: EASE }}
              data-testid="swipe-loop"
              className="w-full"
            >
              <SwipeLoop />
            </motion.div>
          </div>
        </div>

        <div className="max-w-[720px] mb-10 md:mb-14">
          <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#D97757]">
            Capture par capture
          </p>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mt-4 font-display text-[26px] sm:text-[32px] md:text-[38px] text-[#0A0A0A]"
          >
            Une app pensée dans le moindre détail.
          </motion.h3>
        </div>
      </div>

      {/* Carousel — full-bleed for edge feel */}
      <div className="relative">
        <div
          ref={trackRef}
          className="flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-6 no-scrollbar"
          style={{ paddingLeft: "max(1.25rem, calc((100vw - 1280px) / 2 + 2rem))", paddingRight: "max(1.25rem, calc((100vw - 1280px) / 2 + 2rem))" }}
        >
          {SHOTS.map((s, i) => (
            <motion.figure
              key={s.src}
              data-testid={`screenshot-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.05 }}
              className="snap-center shrink-0 w-[220px] sm:w-[260px] md:w-[300px] lg:w-[320px]"
            >
              <div className="mm-iphone">
                <div className="mm-iphone-screen">
                  <img
                    src={s.src}
                    alt={`MamaMatch — ${s.label}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
              <figcaption className="mt-5 px-1">
                <div className="text-[12px] font-medium text-[#6B6660]">
                  0{i + 1} / {SHOTS.length}
                </div>
                <div className="mt-1 text-[16px] font-semibold text-[#0A0A0A]">
                  {s.label}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="mx-auto max-w-[1280px] px-5 md:px-8 mt-8 md:mt-10 flex items-center justify-between">
        <div className="flex gap-1.5" role="tablist" aria-label="Aperçus">
          {SHOTS.map((_, i) => (
            <button
              key={i}
              data-testid={`carousel-dot-${i}`}
              aria-label={`Aperçu ${i + 1}`}
              aria-selected={index === i}
              onClick={() => scrollTo(i)}
              className={`h-1 rounded-full transition-all duration-300 ${
                index === i
                  ? "bg-[#0A0A0A] w-8"
                  : "bg-[rgba(10,10,10,0.15)] w-4"
              }`}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="carousel-prev"
            aria-label="Précédent"
            onClick={() => scrollTo(index - 1)}
            className="btn-ghost w-11 h-11 flex items-center justify-center"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            data-testid="carousel-next"
            aria-label="Suivant"
            onClick={() => scrollTo(index + 1)}
            className="btn-primary w-11 h-11 flex items-center justify-center"
          >
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
